import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import StyledUpdateForm from 'components/organisms/Forms/UpdateForm/UpdateForm.styles';
import Box from 'components/atoms/Layout/Box/Box';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Send from '@mui/icons-material/Send';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import constants from 'helpers/constants';
import {useSnackbar} from 'components/organisms/Providers/SnackbarProvider/SnackbarProvider';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import {useClientCallbacks} from 'services/client/client.utils';
import dom from 'helpers/dom';
import CommentDialog from 'components/organisms/Dialogs/CommentDialog/CommentDialog';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import utils from 'helpers/utils';

const UpdateForm = React.forwardRef((props, ref) => {
  const {
    onDirty,
    onSubmit,
    variant,
    isLoading,
    ...innerProps
  } = useComponentProps({
    variant: 'timeline',
    ...props
  }, 'UpdateForm');

  const innerRef = useRef(null);
  const fieldRef = useRef(null);
  const formRef = useRef(null);

  useImperativeHandle(ref, () => innerRef.current);

  const simple = variant === 'simple';

  const [internalState, setInternalState] = useState({
    dirty: false,
    showMenu: false,
    submitting: false,
    dialogActive: false,
    inputDisabled: true
  });

  const clientCallbacks = useClientCallbacks();

  const snackbar = useSnackbar();
  const dialogControl = useDialogControl();

  const onSubmitEvent = useEffectEvent(onSubmit);
  const handleSubmit = useCallback((values, actions) => {
    setInternalState(utils.updater({
      submitting: true
    }, true));

    onSubmitEvent?.(values['update'])
      .then(() => {
        setInternalState(utils.updater({
          showMenu: false,
          inputDisabled: true
        }, true));
        actions.resetForm();
      })
      .catch(() => {
        snackbar.show('Saving comment failed', null,
            {color: 'error', autoHideDuration: constants.delay.error});
      })
      .finally(() => {
        actions.setSubmitting(false);
        setInternalState(utils.updater({
          submitting: false
        }, true));
      });
  }, [onSubmitEvent, snackbar]);

  const handleChangeDirect = useCallback((e) => {
    setInternalState(utils.updater({
      inputDisabled: !(utils.stripHtml(e.target.value, true)?.length > 0)
    }, true));
  }, []);

  const handleFocus = (e) => {
    if (dom.isPartOfParent(e.target, innerRef.current)) {
      fieldRef.current?.focus();
    }
  };

  const handleSubmitClick = useCallback(() => {
    formRef.current?.submit();
  }, [formRef]);

  const onDirtyEvent = useEffectEvent(onDirty);
  const handleValidating = useCallback((isValidating, isDirty) => {
    onDirtyEvent?.(isDirty || internalState.dialogActive);
    setInternalState(utils.updater({
      dirty: isDirty
    }, true));
  }, [onDirtyEvent, internalState.dialogActive]);

  const fields = useMemo(() => ([{
    name: 'update',
    placeholder: 'Type your comment',
    type: constants.formFieldTypes.richText,
    validation: constants.formFieldValidationTypes.text,
    initial: null,
    postfix: simple ? <ActionIconButton action={{
      icon: Send,
      tooltip: 'Send',
      IconButtonProps: {
        disabled: internalState.inputDisabled || internalState.submitting,
        isLoading: isLoading,
        disableFocusRipple: true
      },
      onClick: () => {
        handleSubmitClick();
      }
    }} /> : null,
    FormFieldProps: {
      ref: fieldRef,
      autoFocus: false,
      hiddenLabel: true,
      variant: simple ? 'outlined' : 'standard',
      size: 'small',
      minRows: 1,
      maxRows: simple ? 6 : 8,
      mentions: [{
        trigger: '@',
        data: (search, callback) => clientCallbacks.members({search, callback})
      }],
      InputLabelProps: {
        shrink: true,
      },
      InputProps: {
        disableUnderline: true
      },
      inputProps: {
        showDialog: true,
        showMenu: internalState.showMenu,
        onToggle: () => {
          setInternalState((current) => {
            return utils.updater({
              ...current,
              showMenu: !current.showMenu
            })(current)
          });
        },
        onDialog: (value) => {
          onDirtyEvent?.(true);
          setInternalState(utils.updater({
            dialogActive: true
          }, true));

          const handleSubmit = (text) => {
            return onSubmitEvent?.(text)
              .then(() => {
                formRef.current?.reset();
                setInternalState(utils.updater({
                  showMenu: false,
                  inputDisabled: true
                }, true));
              });
          }

          const handleClose = () => {
            setInternalState(utils.updater({
              dialogActive: false
            }, true));

            onDirtyEvent?.(internalState.dirty);
            dialogControl.hide();
          }

          dialogControl.show(<CommentDialog text={value}
                                            showMenu={true}
                                            SaveButtonProps={{
                                              children: 'Send',
                                              startIcon: <Icon icon={Send} />
                                            }}
                                            onSubmit={handleSubmit} />, true, handleClose);
        }
      }
    }
  }]), [simple, internalState.inputDisabled, internalState.submitting, internalState.showMenu, internalState.dirty,
    onDirtyEvent, handleSubmitClick, onSubmitEvent, isLoading, clientCallbacks, dialogControl]);

  const sendAction = useMemo(() => ({
    icon: <Icon icon={Send} size="smaller" />,
    tooltip: 'Send',
    onClick: () => {
      handleSubmitClick();
    },
    IconButtonProps: {
      disabled: internalState.inputDisabled || internalState.submitting,
      size: 'smaller',
      density: 'sparse',
      color: 'primary',
      variant: 'contained',
      isLoading: isLoading
    }
  }), [internalState.inputDisabled, internalState.submitting, handleSubmitClick, isLoading]);

  const fieldEl = fieldRef.current?.ref?.current;
  useEffect(() => {
    if (fieldEl) {
      return utils.observeEvent(fieldEl, 'keydown', (e) => {
        if (!(internalState.inputDisabled || internalState.submitting)) {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            handleSubmitClick();
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }, { capture: true });
    }
  }, [fieldEl, internalState.inputDisabled, internalState.submitting, handleSubmitClick]);

  return <StyledUpdateForm ref={innerRef} {...innerProps} onClick={handleFocus}>
    <InlineForm ref={formRef}
                className="UpdateForm-form"
                onSubmit={handleSubmit}
                onValidating={handleValidating}
                onChangeDirect={handleChangeDirect}
                fields={fields}
                isLoading={isLoading}/>
    {!simple ? <Box className="UpdateForm-icons">
      <ActionIconButton action={sendAction} />
    </Box> : null}
  </StyledUpdateForm>
});

UpdateForm.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  inputDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  onDirty: PropTypes.func,
  onSubmit: PropTypes.func,
  variant: PropTypes.oneOfType([PropTypes.oneOf(['timeline', 'simple']), PropTypes.string])
};

export default UpdateForm;
